import { AxiosError, AxiosResponse } from 'axios';
import { createWithEqualityFn } from 'zustand/traditional';

import {
  ApplicantDetails,
  RoleEnum,
  RolesProfile,
  RecommenderDocument,
  MembersDocument,
} from '@youscience/commonapp-service-common';

import { commonAppService } from '@services/commonApp.service';

import useAuthStore from '@store/AuthStore';
import useUserPreferencesStore from '@store/UserPreferencesStore';
import { notify } from '@store/NotificationStore';

import { RecommenderInfo, RecommenderInfoUpdateResponse } from '@interfaces/recommenderWorkspace';
import useAppInitStore from '@store/AppInitStore';

interface CommonAppAdminStore {
  isLoading: boolean;
  applicantId: number | null;
  requestId: string | null;
  members: MembersDocument[];
  applicantDetails: ApplicantDetails;
  roles: RolesProfile[] | undefined;
  userRoles: RoleEnum[] | undefined;
  currentUserRoles: RolesProfile | undefined;
  connectedStudents: RecommenderDocument[];
  recommenderLastUpdated: number;
  getCollegeList: () => Promise<void>;
  getRoles: () => Promise<RolesProfile[]>;
  removeRole: (userId: string, role: RoleEnum) => void;
  getConnectedStudents: () => Promise<void>;
  updateRecommenderInfo: (data: RecommenderInfo, showNotification?: boolean) => Promise<void>;
}

const initialState = {
  isLoading: false,
  applicantId: null,
  requestId: null,
  recommenderLastUpdated: Date.now(),
} as CommonAppAdminStore;

export const useCommonAppAdminStore = createWithEqualityFn<CommonAppAdminStore>()(
  (set, get) => ({
    ...initialState,

    getCollegeList: async () => {
      set({
        isLoading: true,
      });

      const members = await commonAppService.get<unknown, AxiosResponse<[MembersDocument]>>('/college-list');

      set({
        members: members.data,
        isLoading: false,
      });
    },

    removeRole: async (userId: string, role: RoleEnum) => {
      set({
        isLoading: true,
      });

      await commonAppService.deleteAsync<{ userId: string; role: RoleEnum }>(`remove-role/${userId}/${role}`);

      void get().getRoles();

      set({
        isLoading: false,
      });
    },

    getRoles: async () => {
      const { isInitialized } = useAppInitStore.getState();

      if (!isInitialized) {
        await new Promise<void>((resolve) => {
          const unsubscribe = useAppInitStore.subscribe((state) => {
            if (state.isInitialized) {
              unsubscribe();
              resolve();
            }
          });
        });
      }

      set({
        isLoading: true,
      });

      const { userId } = useUserPreferencesStore.getState().userPreferences;

      const roles = await commonAppService.getAsync<RolesProfile[]>('roles');

      const currentUserRoles = roles?.find((role) => role.userId === userId);

      set({
        isLoading: false,
        roles,
        userRoles: currentUserRoles?.roles,
        currentUserRoles,
      });

      return roles;
    },

    getConnectedStudents: async () => {
      set({
        isLoading: true,
      });

      const connectedStudents = await commonAppService.getAsync<[]>('connected-students');

      set({
        connectedStudents,
        isLoading: false,
      });
    },

    updateRecommenderInfo: async (data: RecommenderInfo, showNotification?: boolean) => {
      set({
        isLoading: true,
      });

      try {
        const response = await commonAppService.patchAsync<RecommenderInfoUpdateResponse, RecommenderInfo>(
          '/role',
          data,
        );

        set({
          isLoading: false,
          userRoles: response.roles,
        });

        await useAuthStore.getState().getMe();

        set({
          recommenderLastUpdated: Date.now(),
        });
      } catch (error) {
        const errorMessage = (error as AxiosError<{ error: string }>).response?.data?.error;

        notify({
          message: `Looks like something went wrong. Please try again later. ${errorMessage as string}`,
          severity: 'error',
        });

        set({
          isLoading: false,
        });

        return;
      }

      if (showNotification) {
        notify({
          message: 'Recommender Profile updates have been saved.',
          severity: 'success',
        });
      }
    },
  }),
  Object.is,
);
